import { tagValue, type TaggedValue, type VarType } from "./Blackboard";
import { Ternary } from "./Ternary";

/** One `~grants` declaration from `globals.grants` (compiler dev-spec T1). */
export interface GrantDef {
  id: string;
  type: VarType;
  default?: string;
}

/** Fetched grant payload shape — structurally identical to PlatformAdapter's
 *  `GrantPayload`, redeclared here so the engine doesn't import from platform/. */
export interface GrantPayloadLike {
  grants: Record<string, unknown>;
  expires: string | null;
}

/**
 * Resolves a game's declared `~grants` against a fetched grant payload —
 * dev-spec T3. There is no separate VAR-initialization pass in the compiled
 * program to apply grants "before beat 0" against (top-level VARs lower to
 * ordinary `kind: "decl"` beats in the entry scene) — instead, `Interpreter`
 * consults `get()` at each decl beat's execution point and substitutes the
 * granted value for the beat's own default when one was assigned.
 */
export class GrantLoader {
  private _values = new Map<string, TaggedValue>();

  static empty(): GrantLoader {
    return new GrantLoader();
  }

  /** Build from a fetched payload, keeping only declared, type-compatible
   *  grants. A null payload (404/unauthenticated/network failure) or an
   *  expired one both resolve to an empty loader — every VAR decl then just
   *  falls back to evaluating its own declared default, silently. */
  static from(declared: GrantDef[], payload: GrantPayloadLike | null): GrantLoader {
    const loader = new GrantLoader();
    if (!payload) return loader;
    if (payload.expires) {
      const expiresAt = Date.parse(payload.expires);
      if (!Number.isNaN(expiresAt) && expiresAt <= Date.now()) return loader;
    }
    const declaredById = new Map(declared.map((d) => [d.id, d]));
    for (const [id, raw] of Object.entries(payload.grants)) {
      const def = declaredById.get(id);
      if (!def) continue; // undeclared grant -- ignore (additive/bounded, T4)
      // Ternary crosses the wire as true/false/null (T/F/U) per the project's
      // usual boundary encoding, not as a Ternary instance -- normalize before tagging.
      const value = def.type === "Ternary" ? Ternary.from(raw) : raw;
      loader._values.set(id, tagValue(value));
    }
    return loader;
  }

  /** Tagged value assigned to `varName`, if any. Undefined means "no grant
   *  applies -- use the VAR decl's own default expression". */
  get(varName: string): TaggedValue | undefined {
    return this._values.get(varName);
  }
}
